import { useMutation } from '@apollo/client';
import type { UpdateOrganisationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateOrganisationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useUpdateOrganisationScimTRPC } from './useUpdateOrganisationScimTRPC';

export interface UpdateOrganisationScimWrapperInput {
  OrgKey: string;
  ScimEnabled: boolean;
}

/**
 * Flag-gated wrapper for the `updateOrganisation` (ScimEnabled toggle)
 * mutation. Defaults to the legacy Apollo mutation and delegates to the
 * tRPC variant when the `trpc` feature flag is enabled.
 *
 * Accepts `{ OrgKey, ScimEnabled }` to match the existing call site.
 * The tRPC path drops `OrgKey` because the server reads it from
 * `ctx.orgKey`.
 */
export const useUpdateOrganisationScim = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateOrganisationGraphQL, graphqlState] = useMutation(
    UpdateOrganisationDocument
  );

  const trpcMutation = useUpdateOrganisationScimTRPC();

  const updateOrganisationScim = async (
    input: UpdateOrganisationScimWrapperInput
  ): Promise<UpdateOrganisationMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateOrganisationScim({
        ScimEnabled: input.ScimEnabled,
      });
    }

    const result = await updateOrganisationGraphQL({
      variables: {
        OrgKey: input.OrgKey,
        ScimEnabled: input.ScimEnabled,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update organisation');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateOrganisationScim,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateOrganisationScim,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
